/**
 * Pieces of one JSON tree row: the leaf value, coloured by type (long or
 * multi-line text sits on one line until clicked, then shows in full with its
 * own line breaks; quotes are drawn by CSS, so copying gets the value only),
 * and the hover actions.
 */

import { useState } from "react";
import { rawJson } from "@/lib/json";
import { CopyButton } from "./copy-button";

const LONG = 80;

function StringValue({ text }: { text: string }) {
  const [full, setFull] = useState(false);
  const quoted = "before:content-['\"'] after:content-['\"']";
  if (text.length <= LONG && !text.includes("\n"))
    return <span className={`break-words text-success ${quoted}`}>{text}</span>;
  return (
    <button
      type="button"
      aria-expanded={full}
      title={full ? "Show one line" : "Show all"}
      onClick={() => setFull(!full)}
      className={`block max-w-full min-w-0 cursor-pointer text-left text-success hover:underline
                  hover:decoration-dotted ${quoted} ${full ? "break-words whitespace-pre-wrap" : "truncate"}`}
    >
      {text}
    </button>
  );
}

export function JsonLeaf({ value }: { value: unknown }) {
  if (value === null || value === undefined) return <span className="text-dim italic">null</span>;
  if (value === "") return <span className="text-dim">""</span>;
  if (typeof value === "string") return <StringValue text={value} />;
  if (typeof value === "number") return <span className="text-series-2">{value}</span>;
  if (typeof value === "boolean") return <span className="text-warn">{String(value)}</span>;
  return <span className="text-dim">{Array.isArray(value) ? "[]" : "{}"}</span>;
}

/** Shown on hover or focus: Use (in pick mode), copy value, copy path. */
export function RowActions({
  path,
  value,
  onPick,
}: {
  path: string;
  value: unknown;
  onPick: (() => void) | null;
}) {
  return (
    <span
      className="flex shrink-0 items-center gap-0.5 opacity-0 group-focus-within:opacity-100
                 group-hover:opacity-100"
    >
      {onPick && (
        <button
          type="button"
          aria-label={`Use ${path}`}
          className="rounded-[4px] px-1.5 font-sans text-[11px] font-semibold text-link hover:bg-run-bg"
          onClick={onPick}
        >
          Use
        </button>
      )}
      <CopyButton text={rawJson(value ?? null)} label={`Copy value of ${path}`} />
      <CopyPath path={path} />
    </span>
  );
}

/** Copying the path is a word, not a second copy icon beside the value's. */
function CopyPath({ path }: { path: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      aria-label={`Copy path ${path}`}
      title={path}
      className="rounded-[4px] px-1.5 font-sans text-[11px] text-faint hover:bg-chip hover:text-ink"
      onClick={(e) => {
        e.stopPropagation();
        void navigator.clipboard?.writeText(path).then(() => {
          setDone(true);
          setTimeout(() => setDone(false), 1200);
        });
      }}
    >
      {done ? "Copied" : "Copy path"}
    </button>
  );
}
